<template>
  <Transition
    name="toast"
    appear
    @enter="onEnter"
    @leave="onLeave"
    @after-leave="() => $emit('removed', notification.id)"
  >
    <div 
      v-if="visible"
      class="toast-notification"
      :class="toastClass"
      :role="toastRole"
      :aria-live="ariaLive"
      :aria-atomic="true"
      :aria-label="accessibilityLabel"
      @mouseenter="pauseTimer"
      @mouseleave="resumeTimer"
      @focus="pauseTimer"
      @blur="resumeTimer"
    >
      <NotificationContent
        :notification="notification"
        @expand="handleExpand"
        @collapse="handleCollapse"
        @close="handleClose"
        @action="handleAction"
        class="toast-notification__content"
      />
      
      <!-- Timer Progress Indicator -->
      <div 
        v-if="showProgress && autoDismiss"
        class="toast-notification__timer"
        :style="{ transform: `scaleX(${progressScale})` }"
      />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import NotificationContent from '../molecules/NotificationContent.vue';
import { useNotifications } from '@/composables/useNotifications';
import type { ToastNotification as ToastNotificationData } from '@/types/notifications';

export interface ToastNotificationProps {
  notification: ToastNotificationData;
  /** Auto dismiss duration in milliseconds */
  autoDismiss?: number | false;
  /** Show progress indicator for auto dismiss */
  showProgress?: boolean;
  /** Toast position for animation direction */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}

const props = withDefaults(defineProps<ToastNotificationProps>(), {
  autoDismiss: 5000,
  showProgress: true,
  position: 'top-right',
});

const emit = defineEmits<{
  expand: [id: string];
  collapse: [id: string];
  close: [id: string];
  action: [id: string, actionId: string];
  removed: [id: string];
  timerPaused: [id: string];
  timerResumed: [id: string];
}>();

const { pauseNotificationTimeout, resumeNotificationTimeout, getNotificationRemainingTime } = useNotifications();

const visible = ref(true);
const isPaused = ref(false);
const pausedAt = ref(0);
const animationTick = ref(0); // Reactive trigger for animation updates

let timerId: number | null = null;
let progressTimerId: number | null = null;

const toastClass = computed(() => ({
  [`toast-notification--${props.notification.severity}`]: true,
  [`toast-notification--${props.position}`]: true,
  'toast-notification--expanded': props.notification.expanded,
  'toast-notification--with-progress': props.showProgress && props.autoDismiss,
  'toast-notification--paused': isPaused.value,
  'toast-notification--persistent': !props.autoDismiss,
}));

const toastRole = computed(() => {
  return props.notification.severity === 'error' ? 'alert' : 'status';
});

const ariaLive = computed(() => {
  return props.notification.severity === 'error' ? 'assertive' : 'polite';
});

const accessibilityLabel = computed(() => {
  const { severity, message } = props.notification;
  const messageText = typeof message === 'string' ? message : message.text;
  return `${severity} notification: ${messageText}`;
});

const progressScale = computed(() => {
  if (!props.autoDismiss || typeof props.autoDismiss !== 'number') return 1;
  // Include animationTick in dependency to trigger reactivity
  animationTick.value; // This creates a reactive dependency
  const remaining = getNotificationRemainingTime(props.notification.id);
  return Math.max(0, remaining / props.autoDismiss);
});

const startTimer = () => {
  if (!props.autoDismiss || typeof props.autoDismiss !== 'number') return;
  
  clearTimers();
  
  // NOTE: Do not create our own setTimeout here - the central notification system handles dismissal
  // We only manage the visual progress indicator
  
  // Update progress indicator using requestAnimationFrame for smooth animation
  if (props.showProgress) {
    const updateProgress = () => {
      // Always continue the animation loop
      progressTimerId = requestAnimationFrame(updateProgress);
      
      if (!isPaused.value) {
        // Trigger reactivity for progressScale computed only when not paused
        animationTick.value++;
        
        // Check if we should stop (when time runs out)
        const remaining = getNotificationRemainingTime(props.notification.id);
        if (remaining <= 0) {
          clearTimers();
        }
      }
      // When paused, we keep the loop running but don't update the tick
    };
    updateProgress();
  }
};

const pauseTimer = () => {
  if (!props.autoDismiss || isPaused.value) return;
  
  isPaused.value = true;
  pausedAt.value = Date.now();
  
  clearTimers();
  
  // Pause the central notification timeout
  pauseNotificationTimeout(props.notification.id);
  
  emit('timerPaused', props.notification.id);
};

const resumeTimer = () => {
  if (!props.autoDismiss || !isPaused.value) return;
  
  // Resume the central notification timeout FIRST
  resumeNotificationTimeout(props.notification.id);
  
  isPaused.value = false;
  
  // Force an immediate animation tick update to resume the visual progress
  animationTick.value++;
  
  // Restart the animation loop since it was stopped in pauseTimer
  startTimer();
  
  emit('timerResumed', props.notification.id);
};

const clearTimers = () => {
  if (timerId) {
    window.clearTimeout(timerId);
    timerId = null;
  }
  if (progressTimerId) {
    cancelAnimationFrame(progressTimerId);
    progressTimerId = null;
  }
};

const handleExpand = () => {
  emit('expand', props.notification.id);
};

const handleCollapse = () => {
  emit('collapse', props.notification.id);
};

const handleClose = () => {
  visible.value = false;
  emit('close', props.notification.id);
};

const handleAction = (actionId: string) => {
  emit('action', props.notification.id, actionId);
};

const onEnter = (el: Element) => {
  // Trigger reflow to ensure transition works
  void (el as HTMLElement).offsetHeight;
};

const onLeave = () => {
  clearTimers();
};

// Watch for external notification changes
watch(() => props.notification.expanded, () => {
  // Pause timer when expanded for better UX
  if (props.notification.expanded) {
    pauseTimer();
  } else {
    resumeTimer();
  }
});

// Start timer on mount
onMounted(() => {
  startTimer();
});

// Cleanup on unmount
onUnmounted(() => {
  clearTimers();
});
</script>

<style scoped lang="scss">
.toast-notification {
  position: relative;
  background: var(--vscode-backgroundSecondary);
  border: 1px solid var(--vscode-border);
  border-radius: var(--border-radius-sm);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: var(--spacing-sm);
  max-width: 400px;
  min-width: 300px;
  cursor: default;
  color: var(--vscode-foreground);
  
  // Dark theme adjustments
  [data-theme='dark'] & {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  }
  
  &:focus {
    outline: 1px solid var(--vscode-focusBorder);
    outline-offset: 1px;
  }
  
  &__content {
    position: relative;
    z-index: 1;
  }
  
  &__timer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--vscode-border);
    transform-origin: left center;
    transition: transform 16ms linear;
    opacity: 0.3;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--vscode-button-background);
      opacity: 0.8;
    }
  }
  
  // Severity variants
  &--success {
    border-left-color: var(--notification-success);
    
    .toast-notification__timer::after {
      background: var(--notification-success);
    }
  }
  
  &--warning {
    border-left-color: var(--notification-warning);
    
    .toast-notification__timer::after {
      background: var(--notification-warning);
    }
  }
  
  &--error {
    border-left-color: var(--notification-error);
    
    .toast-notification__timer::after {
      background: var(--notification-error);
    }
  }
  
  &--info {
    border-left-color: var(--notification-info);
    
    .toast-notification__timer::after {
      background: var(--notification-info);
    }
  }
  
  // State modifiers
  &--expanded {
    max-height: 500px;
  }
  
  &--paused {
    .toast-notification__timer {
      animation: pulse 2s infinite;
    }
  }
  
  &--persistent {
    .toast-notification__timer {
      display: none;
    }
  }
}

// Toast transition animations
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

// Position-based enter animations
.toast-notification--top-right {
  &.toast-enter-from {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
  
  &.toast-leave-to {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
}

.toast-notification--top-left {
  &.toast-enter-from {
    transform: translateX(-100%) scale(0.8);
    opacity: 0;
  }
  
  &.toast-leave-to {
    transform: translateX(-100%) scale(0.8);
    opacity: 0;
  }
}

.toast-notification--bottom-right {
  &.toast-enter-from {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
  
  &.toast-leave-to {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
}

.toast-notification--bottom-left {
  &.toast-enter-from {
    transform: translateX(-100%) scale(0.8);
    opacity: 0;
  }
  
  &.toast-leave-to {
    transform: translateX(-100%) scale(0.8);
    opacity: 0;
  }
}

.toast-notification--top-center,
.toast-notification--bottom-center {
  &.toast-enter-from {
    transform: translateY(-20px) scale(0.9);
    opacity: 0;
  }
  
  &.toast-leave-to {
    transform: translateY(-20px) scale(0.9);
    opacity: 0;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}
</style>
